import React, { useState, useContext } from "react";
import styled from "styled-components";
import { Button, Modal, Spin, message } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import { getLenderPacketData } from "../lib/lenderPacketData";
import { generateLenderPacketPdf } from "../utils/lenderPacketPdf";
import { trackEvent } from "../helpers/mixpanel";

/** Wrapper that forces white text/icon, vertical center, and icon 10% smaller */
const LenderPacketButtonWrapper = styled.div`
  .lender-packet-btn.ant-btn {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  .lender-packet-btn.ant-btn,
  .lender-packet-btn.ant-btn .anticon,
  .lender-packet-btn.ant-btn span {
    color: #fff !important;
  }
  .lender-packet-btn.ant-btn .ant-btn-icon {
    display: inline-flex !important;
    align-items: center !important;
  }
`;

/**
 * High-visibility "Generate Lender Packet" button with loading state.
 * Shows "Analyzing Portfolio..." modal while fetching data and generating PDF.
 * @param {Object} props
 * @param {string} [props.month] - YYYY-MM for rent roll
 * @param {string} [props.address] - Override address (e.g. single property)
 * @param {string} [props.propertyPhotoUrl] - Optional property image URL for page 1
 * @param {'primary'|'default'} [props.type] - Button type
 * @param {string} [props.size] - Button size
 * @param {boolean} [props.block] - Full width
 * @param {React.CSSProperties} [props.style] - Button style
 */
const GenerateLenderPacketButton = ({
  month,
  address,
  propertyPhotoUrl,
  type = "primary",
  size = "large",
  block = false,
  style,
  ...rest
}) => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const landlordName = user?.displayName || user?.email || "Borrower";

  const handleClick = async () => {
    setLoading(true);
    try {
      const data = await getLenderPacketData(month, address, propertyPhotoUrl);
      generateLenderPacketPdf({
        ...data,
        landlordName,
        filename: `lender-packet-${new Date().toISOString().slice(0, 10)}.pdf`,
      });
      message.success("Lender packet downloaded.");
      trackEvent("lender_packet_generated", {
        has_photo: !!data.propertyPhotoUrl,
        rent_roll_count: data.rentRoll?.length ?? 0,
      });
    } catch (err) {
      console.error("Lender packet error:", err);
      message.error("Failed to generate lender packet. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LenderPacketButtonWrapper>
        <Button
          className="lender-packet-btn"
          type={type}
          size={size}
          block={block}
          icon={
            <FilePdfOutlined
              style={{ fontSize: 12, filter: "brightness(0) invert(1)" }}
            />
          }
          loading={loading}
          onClick={handleClick}
          style={{
            backgroundColor: style?.backgroundColor ?? "#2c5f2d",
            borderColor: "#2c5f2d",
            paddingTop: 10,
            paddingBottom: 12,
            height: "100%",
            ...style,
          }}
          {...rest}
        >
          Generate Lender Packet
        </Button>
      </LenderPacketButtonWrapper>
      <Modal
        open={loading}
        footer={null}
        closable={false}
        maskClosable={false}
        width={320}
        centered
        styles={{ body: { padding: "24px 16px", textAlign: "center" } }}
      >
        <Spin size="large" />
        <div style={{ marginTop: 16, fontSize: 15, color: "#1E293B" }}>
          Analyzing Portfolio…
        </div>
        <div style={{ marginTop: 8, fontSize: 13, color: "#6B7280" }}>
          Preparing your lender packet
        </div>
      </Modal>
    </>
  );
};

export default GenerateLenderPacketButton;
